import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../services/apiService'
import {
  BellIcon,
  CheckIcon,
  TrashIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const NotificationsPage = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    loadNotifications()
  }, [])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      // Mock data for demo
      const mockNotifications = [
        {
          id: 1,
          type: 'maintenance_overdue',
          title: 'Manutenção Urgente',
          message: 'Caminhão DAF-2156 precisa de revisão imediata',
          priority: 'urgent',
          read: false,
          created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          action_url: '/vehicles/2'
        },
        {
          id: 2,
          type: 'payment_overdue',
          title: 'Pagamento em Atraso',
          message: 'Mineração XYZ - R$ 9.760 vencido em 15/10',
          priority: 'high',
          read: false,
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          action_url: '/contracts/5'
        },
        {
          id: 3,
          type: 'contract_expiring',
          title: 'Contrato Expirando',
          message: 'Construtora ABC expira em 7 dias',
          priority: 'normal',
          read: false,
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          action_url: '/contracts/4'
        },
        {
          id: 4,
          type: 'success',
          title: 'Pagamento Recebido',
          message: 'R$ 13.800 - Construtora ABC recebido com sucesso',
          priority: 'normal',
          read: true,
          created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 5,
          type: 'info',
          title: 'Nova Manutenção Agendada',
          message: 'Trator Valtra BH180 - manutenção preventiva dia 25/10',
          priority: 'normal',
          read: true,
          created_at: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 6,
          type: 'vehicle_health_alert',
          title: 'Alerta de Manutenção',
          message: 'Escavadeira CAT-320 com score baixo (25/100)',
          priority: 'medium',
          read: false,
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          action_url: '/vehicles/1'
        }
      ]
      
      setNotifications(mockNotifications)
      setUnreadCount(mockNotifications.filter(n => !n.read).length)
    } catch (error) {
      console.error('Erro ao carregar notificações:', error)
      toast.error('Erro ao carregar notificações')
    } finally {
      setLoading(false)
    }
  }

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.read
    if (filter === 'urgent') return notification.priority === 'urgent'
    if (filter === 'high') return notification.priority === 'high'
    return true
  })

  const getNotificationIcon = (type, priority) => {
    if (priority === 'urgent') return '🚨'
    
    const icons = {
      maintenance_overdue: '🔧',
      payment_overdue: '💰',
      contract_expiring: '📋',
      success: '✅',
      info: '📢',
      warning: '⚠️',
      error: '❌',
      vehicle_health_alert: '🚛'
    }
    return icons[type] || '📢'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
      high: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20',
      medium: 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
      normal: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
      low: 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/20'
    }
    return colors[priority] || colors.normal
  }

  const formatTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'agora'
    if (diffInMinutes < 60) return `há ${diffInMinutes} min`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `há ${diffInHours}h`
    
    const diffInDays = Math.floor(diffInHours / 24)
    return `há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`
  }

  const handleMarkAsRead = async (id) => {
    try {
      setNotifications(prev => 
        prev.map(n => 
          n.id === id ? { ...n, read: true } : n
        )
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
      toast.success('Notificação marcada como lida')
    } catch (error) {
      console.error('Erro:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      )
      setUnreadCount(0)
      toast.success('Todas as notificações marcadas como lidas')
    } catch (error) {
      console.error('Erro:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      setNotifications(prev => prev.filter(n => n.id !== id))
      toast.success('Notificação excluída')
    } catch (error) {
      console.error('Erro:', error)
    }
  }

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id)
    }
    
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando notificações...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            🔔 Central de Notificações
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Alertas, avisos e atualizações do sistema em tempo real
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <CheckIcon className="w-4 h-4" />
              <span>Marcar todas como lidas</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <BellIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-2xl font-bold text-blue-600">{notifications.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Não Lidas</p>
              <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
              <div className="text-2xl">🚨</div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Urgentes</p>
              <p className="text-2xl font-bold text-orange-600">
                {notifications.filter(n => n.priority === 'urgent').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Lidas Hoje</p>
              <p className="text-2xl font-bold text-green-600">
                {notifications.filter(n => n.read).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <div className="flex space-x-2">
            {[
              { key: 'all', label: 'Todas', count: notifications.length },
              { key: 'unread', label: 'Não Lidas', count: unreadCount },
              { key: 'urgent', label: 'Urgentes', count: notifications.filter(n => n.priority === 'urgent').length },
              { key: 'high', label: 'Alta Prioridade', count: notifications.filter(n => n.priority === 'high').length }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-blue-600'
                }`}
              >
                {label} {count > 0 && <span className="ml-1">({count})</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`card border-l-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
              getPriorityColor(notification.priority)
            } ${!notification.read ? 'ring-1 ring-blue-200 dark:ring-blue-800' : 'opacity-75'}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start space-x-4">
              <div className="text-2xl flex-shrink-0 mt-1">
                {getNotificationIcon(notification.type, notification.priority)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${
                      !notification.read 
                        ? 'text-gray-900 dark:text-white' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {notification.title}
                      {!notification.read && (
                        <span className="ml-2 w-2 h-2 bg-blue-600 rounded-full inline-block"></span>
                      )}
                    </h3>
                    <p className={`mt-1 ${
                      !notification.read 
                        ? 'text-gray-700 dark:text-gray-300' 
                        : 'text-gray-500 dark:text-gray-500'
                    }`}>
                      {notification.message}
                    </p>
                    <div className="flex items-center flex-wrap gap-3 mt-3">
                      <span className="text-sm text-gray-500 dark:text-gray-500">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        notification.priority === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        notification.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                        notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      }`}>
                        {notification.priority.toUpperCase()}
                      </span>
                      {notification.entity_type === 'contract' && (
                        <span className="text-xs px-2 py-1 rounded-full font-medium bg-indigo-100 text-indigo-700">
                          📄 Ver Contrato
                        </span>
                      )}
                      {notification.entity_type === 'inspection' && (
                        <span className="text-xs px-2 py-1 rounded-full font-medium bg-teal-100 text-teal-700">
                          🔍 Ver Vistoria
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleMarkAsRead(notification.id)
                        }}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                        title="Marcar como lida"
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(notification.id)
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredNotifications.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Nenhuma notificação encontrada
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filter === 'unread' 
                ? 'Todas as notificações foram lidas!' 
                : 'Não há notificações com os filtros selecionados.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage